/**
 * Root Layout — Outermost route component
 *
 * Replaces the Next.js app/[locale]/layout.tsx.
 * Outlet renders the matched child route.
 * AuthModalSlot renders the auth dialog when triggered.
 */
import { buildLocalePath, localeFromPath } from '@letsgethome/lib/weburl';
import { plainText, plaintextString } from '@letsgethome/types/branded';
import { Toaster } from '@letsgethome/ui/sonner';
import { Outlet, ScrollRestoration } from 'react-router';

import { AuthModal } from '../features/auth';
import { SeoHead } from '../shared/components/SeoHead';
import { useReportedRouteError } from '../shared/hooks/useReportedRouteError';
import { usePathname } from '../shared/i18n/routing';
import { ERROR_SHELL_ROBOTS } from '../shared/lib/seo/robots';
import { AppProviders } from '../shared/providers/AppProviders';
import { useAuth } from '../shared/providers/AuthProvider';

function AuthModalSlot() {
  const { isModalOpen, closeAuthModal, googleClientId } = useAuth();
  if (!isModalOpen) return null;

  return (
    <AuthModal isOpen={isModalOpen} onClose={closeAuthModal} googleClientId={googleClientId} />
  );
}

export function Component() {
  return (
    <AppProviders>
      <Outlet />
      <AuthModalSlot />
      <Toaster position="top-center" richColors />
      <ScrollRestoration />
    </AppProviders>
  );
}

// Hardcoded fallback copy for the root error boundary. Intentionally NOT routed
// through i18n: this renders on a route/render failure where the translation
// context may be unavailable. Held as constants so the strings sit outside
// JSX-text position, clearing i18next/no-literal-string without suppression.
// Error-boundary fallback copy. Intentionally English via plaintextString():
// this ErrorBoundary catches crashes that may include the i18n provider itself,
// so calling t() here could re-throw in the very error state we are rendering.
// (Flagged for product review: the 404 branch is a normal user-facing state and
// could be localized if rendered below a guaranteed-live IntlProvider.)
const ERROR_COPY = {
  title404: plaintextString('404'),
  titleError: plaintextString('Error'),
  body404: plaintextString('Page not found.'),
  bodyError: plaintextString('Something went wrong. Please try again.'),
  goHome: plaintextString('Go home'),
  // `plainText` (not `plaintextString`): SeoHead's `title` is `LocalizedText`.
  // Deliberately-English fallback copy, same rationale as the block above.
  seoTitle404: plainText('404 — Page not found'),
  seoTitleError: plainText('Something went wrong'),
  // SeoHead only emits <meta name="description"> when it HAS one, so omitting
  // this left the boundary as the site's single description-less page (the
  // Ahrefs "Meta description tag missing or empty" finding, 1 URL). Harmless
  // on a noindex page, but a head that is complete everywhere is one fewer
  // exception to reason about.
  seoDescription404: plainText(
    'This page could not be found. Browse homes for rent on LetsGetHome.'
  ),
  seoDescriptionError: plainText('This page could not be loaded. Please try again in a moment.'),
} as const;

export function ErrorBoundary() {
  // A raw <a> (not Link): the root boundary catches errors in the whole route
  // tree, so a FULL-document reload to home is the safe reset. Localize the
  // target so the reset stays in-language (the only raw internal anchor in the
  // app — every other internal nav goes through the locale-aware Link).
  const home = buildLocalePath(localeFromPath(usePathname()), '/');

  // Reported through the SHARED seam `FeatureErrorBoundary` uses. This copy had
  // every defect that one was fixed for — logging during RENDER, at ERROR level
  // for an expected 404, via `new Error(String(error))`, which for an
  // `ErrorResponse` is the literal message `Error: [object Object]` — and it
  // sees MORE of that traffic than the feature boundary does, not less: it is
  // the boundary the `locale` wrapper carries, so it is what renders whenever a
  // refusal is filed above the feature subtree. See `useReportedRouteError` for
  // the argument, including why the level split is by STATUS.
  const routeError = useReportedRouteError('ErrorBoundary');
  const is404 = routeError?.status === 404;

  return (
    <div className="flex min-h-screen items-center justify-center">
      {/* Error pages must still carry a head: a <title> + robots noindex.
          Paired with the `pageStatus` seam (server/http-status.ts) this closes
          the "headless 404 served as HTTP 200" class — the boundary used to
          ship no <title>/<meta> at all, so crawlers indexed empty duplicate
          shells. SeoHead sits below renderApp's always-mounted IntlProvider,
          so it is safe to render even in the error state. */}
      <SeoHead
        title={is404 ? ERROR_COPY.seoTitle404 : ERROR_COPY.seoTitleError}
        description={is404 ? ERROR_COPY.seoDescription404 : ERROR_COPY.seoDescriptionError}
        robots={ERROR_SHELL_ROBOTS}
      />
      <div className="text-center">
        <h1 className="text-4xl font-bold">
          {is404 ? ERROR_COPY.title404 : ERROR_COPY.titleError}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          {is404 ? ERROR_COPY.body404 : ERROR_COPY.bodyError}
        </p>
        <a href={home} className="mt-6 inline-block text-primary underline">
          {ERROR_COPY.goHome}
        </a>
      </div>
    </div>
  );
}
